import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CheckCircle2, RefreshCw, Send, Trash2 } from 'lucide-react';
import type { Draft } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { fullDate, relativeTime } from '../../lib/format';
import { DraftStatusChip, draftStatusMeta } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/Overlay';

/** Outgoing drafts for a thread that are no longer editable: scheduled / queued / sending / accepted / failed / stale… */
export function DraftStatusList({ drafts, threadId }: { drafts: Draft[]; threadId: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const invalidate = () => {
    if (threadId) void qc.invalidateQueries({ queryKey: qk.thread(threadId) });
    void qc.invalidateQueries({ queryKey: qk.draftsRoot });
  };
  const retry = useMutation({
    mutationFn: (d: Draft) => api('POST /api/drafts/:id/send', { params: { id: d.id }, body: { signatureId: d.signatureId } }),
    onSuccess: () => toast.success('Queued again — the server re-checks the thread before sending.'),
    onError: (e) => toast.error(e),
    onSettled: invalidate,
  });
  const approve = useMutation({
    mutationFn: (id: string) => api('POST /api/drafts/:id/approve', { params: { id } }),
    onSuccess: () => toast.success('Approved and queued.'),
    onError: (e) => toast.error(e),
    onSettled: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE /api/drafts/:id', { params: { id } }),
    onSuccess: () => toast.success('Cancelled.'),
    onError: (e) => toast.error(e),
    onSettled: invalidate,
  });
  if (!drafts.length) return null;
  return (
    <ul className="space-y-2" aria-label="Outgoing replies">
      {drafts.map((d) => {
        const note = draftStatusMeta[d.status].note;
        const cancellable = d.status === 'scheduled' || d.status === 'pending_approval' || d.status === 'stale' || d.status === 'failed';
        return (
          <li key={d.id} className="rounded-card border border-hairline bg-surface px-4 py-3 shadow-card">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-raised text-muted">
                {d.status === 'scheduled' ? <CalendarClock className="h-4 w-4" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[0.8125rem] font-medium text-ink">
                  {d.kind === 'acknowledgement' ? 'Acknowledgement' : d.kind === 'auto_reply' ? 'Automatic reply' : 'Reply'} to {d.to.map((a) => a.email).join(', ') || '—'}
                </p>
                <p className="text-xs text-muted" title={fullDate(d.updatedAt)}>
                  {d.status === 'scheduled' && d.scheduledAt ? `Sends ${fullDate(d.scheduledAt)}` : relativeTime(d.updatedAt)}
                  {note && <> · {note}</>}
                </p>
                {d.error && <p className="mt-0.5 text-xs text-danger-ink">{d.error}</p>}
              </div>
              <DraftStatusChip status={d.status} />
              {(d.status === 'failed' || d.status === 'pending_approval' || cancellable) && (
                <div className="flex shrink-0 items-center gap-1.5">
                  {d.status === 'failed' && (
                    <Button size="sm" variant="primary" icon={<RefreshCw className="h-3.5 w-3.5" />} loading={retry.isPending} onClick={() => retry.mutate(d)}>
                      Retry
                    </Button>
                  )}
                  {d.status === 'pending_approval' && (
                    <Button size="sm" variant="primary" icon={<CheckCircle2 className="h-3.5 w-3.5" />} loading={approve.isPending} onClick={() => approve.mutate(d.id)}>
                      Approve & send
                    </Button>
                  )}
                  {cancellable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Trash2 className="h-3.5 w-3.5" />}
                      onClick={async () => {
                        if (await confirm({ title: 'Cancel this reply?', message: 'It will not be sent.', confirmLabel: 'Cancel reply', danger: true })) remove.mutate(d.id);
                      }}
                    >
                      {d.status === 'scheduled' ? 'Cancel schedule' : 'Discard'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
